// Pestaña "Simulacros" del Plan de Emergencias v2 (PE-B2, PO-17). Lista con estado, fecha, tipo, alcance, tiempo total
// y semáforo; programar (con evento del Calendario SSO); registrar el resultado con la comparación frente al plan
// (trazas); acciones de mejora propuestas que el usuario confirma (emergency_plan_actions, clave simulacro:<id>:<n>);
// evidencias; informe en PDF con firmas (K6: en pantalla, papel firmado o FirmaEC); bloqueo tras firmar; reabrir solo el
// administrador con motivo; e informe anual firmado. Sin alert ni confirm del navegador.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignaturePad, { SignaturePadHandle } from '../../components/permits/SignaturePad';
import { logAudit } from '../../auditLog';
import { blobADataUrl } from '../../lib/permisos-evidencia';
import { sha256Hex } from '../../lib/psicosocial-codigos';
import type { AccionEditable, FilaAccion } from '../../lib/pe/pe-acciones';
import { ETIQUETA_ESTADO_ACCION, validarAccion } from '../../lib/pe/pe-acciones';
import type { FilaPlan } from '../../lib/pe/pe-datos';
import { ACEPTA_EVIDENCIA, tamanoLegible } from '../../lib/pe/pe-evidencias';
import {
  accionesPropuestas, alcanceTexto, compararConPlan, contenidoParaFirma, editableDesdePropuesta, esClaveSimulacro, estadoPO17,
  ETIQUETA_ESTADO, ETIQUETA_METODO_FIRMA, ETIQUETA_ROL_FIRMA, ETIQUETA_SEMAFORO, ETIQUETA_TIPO, faltantesParaFirmar, fechaEc,
  fechaSimulacro, FilaSimulacro, formatoDuracion, horaASegundos, horaDesdeCronometro, MetodoFirma, ocupantesEsperados, origenSimulacro,
  PlanEvacuacion, PropuestaSimulacro, resumenAnual, RolFirma, SectorMin, semaforo, siguienteClaveManual, simulacrosDelAno, tiempoTotal,
  TIPOS_SIMULACRO, TipoSimulacro, tipoTexto, validarProgramacion, validarResultado,
} from '../../lib/pe/pe-simulacros';
import {
  borrarEvidenciaSimulacro, borrarSimulacro, cargarBrigadistasPresentes, cargarEvidenciasSimulacros, cargarInformesAnuales, cargarSimulacros,
  FilaEvidenciaSimulacro, FilaInformeAnual, FirmaParaSubir, firmarInforme, guardarBrigadistasPresentes, guardarInformeAnual, guardarSimulacro,
  nombreArchivoInforme, programarSimulacro, reabrirSimulacro, SimulacroEditable, subirEvidenciaSimulacro, urlArchivoSimulacro,
} from '../../lib/pe/pe-simulacros-datos';
import { FirmaInformePDF, generarInformeAnualPDF, generarInformeSimulacroPDF } from '../../lib/pe/pe-simulacros-pdf';
import { Campo, ModalConfirmar, NumeroValor, Semaforo, TrazaItem } from './ComponentesPE';
import { plural } from '../../lib/plurales';

export interface PropsSimulacros {
  companyId: string;
  empresa: string;
  plan: FilaPlan;
  sectores: SectorMin[];
  planEvacuacion: PlanEvacuacion | null;
  amenazas: { clave: string; nombre: string }[];
  trabajadores: { id: string; full_name: string }[];
  brigada: string[] | null;            // worker_id de la brigada del plan (emergency_brigade_members)
  acciones: FilaAccion[];
  usuarioId: string | null;
  puedeEditar: boolean;                // admin y manager
  puedeBorrar: boolean;                // admin (borrar, reabrir)
  quito: boolean;
  onGuardarAccion: (a: AccionEditable) => Promise<boolean>;
  onCambio: () => void;                // vuelve a sincronizar el Checklist (PO-17)
  hoy?: Date;
}

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const hoyIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// Descargas (URL con Content-Disposition: attachment) en la misma pestaña; ver una evidencia, en una pestaña nueva
// con noopener (PE-B2b: antes sacaba al usuario de la app), igual que las evidencias de las acciones (paso 5).
function descargarUrl(url: string, nuevaPestana = false) {
  const a = document.createElement('a'); a.href = url; a.rel = 'noopener noreferrer';
  if (nuevaPestana) a.target = '_blank';
  document.body.appendChild(a); a.click(); a.remove();
}

const VACIO: SimulacroEditable = {
  status: 'programado', scheduled_date: null, performed_date: null, start_time: null, drill_type: 'evacuacion', drill_type_other: null,
  objective: null, scenario: null, threat_key: null, location: null, scope_all: true, scope_sector_ids: [], announced: true,
  coordinator_worker_id: null, coordinator_role: null, observers: null, participants_count: null, expected_occupants: null, assembly_count: null,
  alarm_time: null, evacuation_start_time: null, last_arrival_time: null, incidents_notes: null, lessons_learned: null, conclusion: null,
};
const CAMPOS = Object.keys(VACIO) as (keyof SimulacroEditable)[];
const editableDe = (s: FilaSimulacro): SimulacroEditable & { id: string } =>
  ({ id: s.id, ...Object.fromEntries(CAMPOS.map(k => [k, (s as unknown as Record<string, unknown>)[k] ?? (VACIO as unknown as Record<string, unknown>)[k]])) } as SimulacroEditable & { id: string });
const mismo = (a: SimulacroEditable, b: SimulacroEditable) => CAMPOS.every(k => JSON.stringify(a[k] ?? null) === JSON.stringify(b[k] ?? null));
// 'HH:MM' del input de hora → 'HH:MM:SS'
const hora = (v: string) => (!v ? null : v.length === 5 ? `${v}:00` : v);

// ── Firmas (K6): firma en pantalla con nombre, cédula, fecha y hora y hash; o papel firmado o PDF con FirmaEC ──

interface FirmanteForm { nombre: string; cedula: string; conTrazo: boolean; archivo: File | null }
type Pads = Record<RolFirma, React.RefObject<SignaturePadHandle | null>>;
const firmanteVacio = (): FirmanteForm => ({ nombre: '', cedula: '', conTrazo: false, archivo: null });

function FormFirmas({ metodo, setMetodo, firmantes, setFirmantes, pads, ocupado }: {
  metodo: MetodoFirma; setMetodo: (m: MetodoFirma) => void; firmantes: Record<RolFirma, FirmanteForm>;
  setFirmantes: (f: Record<RolFirma, FirmanteForm>) => void; pads: Pads; ocupado: boolean;
}) {
  return (
    <>
      <Campo etiqueta="Cómo se firma">
        <select value={metodo} disabled={ocupado} onChange={e => setMetodo(e.target.value as MetodoFirma)}>
          {(['pantalla', 'papel', 'firmaec'] as MetodoFirma[]).map(m => <option key={m} value={m}>{ETIQUETA_METODO_FIRMA[m]}</option>)}
        </select>
      </Campo>
      {(['sst', 'representante'] as RolFirma[]).map(rol => (
        <div key={rol} className="pe2-card pe2-sub-card">
          <h4>{ETIQUETA_ROL_FIRMA[rol]}{rol === 'representante' ? ' (opcional)' : ''}</h4>
          <Campo etiqueta={`Nombre (${ETIQUETA_ROL_FIRMA[rol]})`}>
            <input type="text" value={firmantes[rol].nombre} disabled={ocupado} onChange={e => setFirmantes({ ...firmantes, [rol]: { ...firmantes[rol], nombre: e.target.value } })} />
          </Campo>
          <Campo etiqueta={`Cédula (${ETIQUETA_ROL_FIRMA[rol]})`}>
            <input type="text" inputMode="numeric" maxLength={10} value={firmantes[rol].cedula} disabled={ocupado}
              onChange={e => setFirmantes({ ...firmantes, [rol]: { ...firmantes[rol], cedula: e.target.value.replace(/\D/g, '') } })} />
          </Campo>
          {metodo === 'pantalla' ? (
            // El componente trae su propio botón "Borrar firma" (PE-B2b: había otro repetido).
            <SignaturePad ref={pads[rol]} disabled={ocupado} onChange={vacio => setFirmantes({ ...firmantes, [rol]: { ...firmantes[rol], conTrazo: !vacio } })} />
          ) : (
            <Campo etiqueta={metodo === 'papel' ? 'Papel firmado (foto o PDF escaneado)' : 'PDF firmado con FirmaEC'}>
              <input type="file" accept={metodo === 'papel' ? ACEPTA_EVIDENCIA : '.pdf,application/pdf'} disabled={ocupado}
                aria-label={`Archivo firmado (${ETIQUETA_ROL_FIRMA[rol]})`}
                onChange={e => setFirmantes({ ...firmantes, [rol]: { ...firmantes[rol], archivo: e.target.files?.[0] ?? null } })} />
            </Campo>
          )}
        </div>
      ))}
    </>
  );
}

// Firmantes con datos: el responsable de SST siempre; el representante solo si escribió su nombre.
function firmantesActivos(f: Record<RolFirma, FirmanteForm>): RolFirma[] {
  return (['sst', 'representante'] as RolFirma[]).filter(rol => rol === 'sst' || f[rol].nombre.trim() !== '');
}
function faltaTrazo(metodo: MetodoFirma, f: Record<RolFirma, FirmanteForm>): string[] {
  return firmantesActivos(f).flatMap(rol => (metodo === 'pantalla' ? !f[rol].conTrazo : !f[rol].archivo)
    ? [metodo === 'pantalla' ? `Falta el trazo de la firma (${ETIQUETA_ROL_FIRMA[rol]}).` : `Falta el documento firmado (${ETIQUETA_ROL_FIRMA[rol]}).`] : []);
}

// Arma las firmas: hash SHA-256 del contenido + firmante, trazo PNG o archivo, y la imagen para el PDF.
async function prepararFirmas(metodo: MetodoFirma, f: Record<RolFirma, FirmanteForm>, pads: Pads,
  contenido: (x: { rol: RolFirma; nombre: string; cedula: string; firmado_en: string }) => string) {
  const ahora = new Date().toISOString();
  const subir: FirmaParaSubir[] = []; const pdf: FirmaInformePDF[] = [];
  for (const rol of firmantesActivos(f)) {
    const base = { rol, nombre: f[rol].nombre.trim(), cedula: f[rol].cedula.trim(), firmado_en: ahora };
    const hash = await sha256Hex(contenido(base));
    const png = metodo === 'pantalla' ? await pads[rol].current?.toBlob() ?? null : null;
    if (metodo === 'pantalla' && !png) throw new Error(`Falta la firma de ${ETIQUETA_ROL_FIRMA[rol]}.`);
    subir.push({ rol, nombre: base.nombre, cedula: base.cedula, firmadoEn: ahora, hash, png, archivo: metodo === 'pantalla' ? null : f[rol].archivo });
    pdf.push({ rol, nombre: base.nombre, cedula: base.cedula, firmadoEn: ahora, hash, imagen: png ? await blobADataUrl(png) : null, archivo: metodo !== 'pantalla' });
  }
  return { subir, pdf, hashInforme: await sha256Hex(subir.map(x => x.hash).join('|')) };
}

// ── Pantalla ──

export default function Simulacros(p: PropsSimulacros) {
  const hoy = useMemo(() => p.hoy ?? new Date(), [p.hoy]);
  const [lista, setLista] = useState<FilaSimulacro[]>([]);
  const [evidencias, setEvidencias] = useState<FilaEvidenciaSimulacro[]>([]);
  const [anuales, setAnuales] = useState<FilaInformeAnual[]>([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState('');
  const [error, setError] = useState(''); const [ok, setOk] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [seleccion, setSeleccion] = useState<string | 'nuevo' | null>(null);
  const [borrador, setBorrador] = useState<SimulacroEditable & { id?: string }>(VACIO);
  const [presentes, setPresentes] = useState<string[]>([]);
  const [presentesAntes, setPresentesAntes] = useState<string[]>([]);
  const [cronometro, setCronometro] = useState(false);
  const [crono, setCrono] = useState({ inicio: '', llegada: '' });
  const [borrar, setBorrar] = useState<FilaSimulacro | null>(null);
  const [borrarEvid, setBorrarEvid] = useState<FilaEvidenciaSimulacro | null>(null);
  const [firmar, setFirmar] = useState(false);
  const [reabrir, setReabrir] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [anualAbierto, setAnualAbierto] = useState(false);
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [metodo, setMetodo] = useState<MetodoFirma>('pantalla');
  const [firmantes, setFirmantes] = useState<Record<RolFirma, FirmanteForm>>({ sst: firmanteVacio(), representante: firmanteVacio() });
  const padSst = useRef<SignaturePadHandle>(null); const padRep = useRef<SignaturePadHandle>(null);
  const pads = { sst: padSst, representante: padRep };
  const [propuestasForm, setPropuestasForm] = useState<Record<string, { obligatoria: boolean; cargo: string; fecha: string; responsableId: string }>>({});
  const [hallazgo, setHallazgo] = useState('');

  const recargar = useCallback(async () => {
    try {
      const [l, ev, an] = await Promise.all([cargarSimulacros(p.companyId), cargarEvidenciasSimulacros(p.companyId), cargarInformesAnuales(p.companyId)]);
      setLista(l); setEvidencias(ev); setAnuales(an); setErrorCarga('');
      return l;
    } catch (e) {
      console.error('cargarSimulacros', e);
      setErrorCarga(msg(e));
      return null;
    } finally { setCargando(false); }
  }, [p.companyId]);
  useEffect(() => { recargar(); }, [recargar]);

  const fila = seleccion && seleccion !== 'nuevo' ? lista.find(s => s.id === seleccion) ?? null : null;
  const bloqueado = !!fila?.report_signed_at;
  const ro = !p.puedeEditar || bloqueado;
  const actual = useMemo((): FilaSimulacro | null => (fila ? { ...fila, ...borrador, id: fila.id } as FilaSimulacro : null), [fila, borrador]);
  const sucio = !!fila && !mismo(editableDe(fila), borrador);
  const presentesCambiaron = JSON.stringify([...presentes].sort()) !== JSON.stringify([...presentesAntes].sort());
  const brigadaPlan = p.brigada ? p.brigada.length : null;
  const nombreTrabajador = (id: string | null) => p.trabajadores.find(t => t.id === id)?.full_name ?? '';

  const comparar = useCallback((s: FilaSimulacro, presentesN: number | null) =>
    compararConPlan(s, p.planEvacuacion, presentesN, brigadaPlan), [p.planEvacuacion, brigadaPlan]);
  const comparaciones = useMemo(() => (actual && actual.status === 'ejecutado' ? comparar(actual, presentes.length) : []), [actual, comparar, presentes.length]);
  const clavesGuardadas = useMemo(() => new Set(p.acciones.map(a => a.source_key).filter((k): k is string => !!k)), [p.acciones]);
  const propuestas: PropuestaSimulacro[] = useMemo(() => (fila && actual && !sucio && fila.status === 'ejecutado' ? accionesPropuestas(actual, comparaciones, clavesGuardadas) : []),
    [fila, actual, sucio, comparaciones, clavesGuardadas]);
  const accionesDe = (id: string) => p.acciones.filter(a => esClaveSimulacro(a.source_key) && a.source_key!.split(':')[1] === id);
  const evidenciasDe = (id: string) => evidencias.filter(e => e.drill_id === id);
  const po17 = useMemo(() => estadoPO17(lista, hoy, p.quito), [lista, hoy, p.quito]);

  function abrir(s: FilaSimulacro | 'nuevo') {
    setError(''); setOk(''); setHallazgo(''); setPropuestasForm({}); setCronometro(false); setCrono({ inicio: '', llegada: '' });
    if (s === 'nuevo') { setSeleccion('nuevo'); setBorrador({ ...VACIO, scheduled_date: hoyIso(hoy) }); setPresentes([]); setPresentesAntes([]); return; }
    setSeleccion(s.id); setBorrador(editableDe(s));
    cargarBrigadistasPresentes(s.id).then(w => { setPresentes(w); setPresentesAntes(w); }).catch(e => { console.error('brigadistas', e); setPresentes([]); setPresentesAntes([]); });
  }
  const set = <K extends keyof SimulacroEditable>(k: K, v: SimulacroEditable[K]) => setBorrador(b => ({ ...b, [k]: v }));

  function registrarResultado() {
    setBorrador(b => {
      const esperado = b.expected_occupants ?? ocupantesEsperados(p.sectores, b.scope_all, b.scope_sector_ids).total;
      // PE-B2b: por defecto, hoy (no la fecha programada, que puede ser futura).
      return { ...b, status: 'ejecutado', performed_date: b.performed_date ?? hoyIso(hoy), expected_occupants: esperado };
    });
  }

  async function guardar() {
    setError(''); setOk('');
    const errores = borrador.status === 'ejecutado' ? validarResultado({ ...(actual ?? (borrador as FilaSimulacro)), ...borrador } as FilaSimulacro, hoy) : validarProgramacion(borrador);
    if (errores.length) { setError(errores.join(' ')); return; }
    setOcupado(true);
    try {
      if (seleccion === 'nuevo') {
        const r = await programarSimulacro(p.companyId, p.plan.id, borrador, p.usuarioId);
        logAudit({ company_id: p.companyId, action: 'created', entity_type: 'emergency_drill', entity_id: r.fila.id, entity_name: origenSimulacro(r.fila),
          details: { evento: 'simulacro_programado', tipo: r.fila.drill_type, fecha: r.fila.scheduled_date } });
        const l = await recargar();
        const nueva = l?.find(x => x.id === r.fila.id) ?? r.fila;
        abrir(nueva);
        setOk(r.avisoCalendario ?? 'Simulacro programado. Quedó en el Calendario SSO con su recordatorio.');
      } else if (fila) {
        const g = await guardarSimulacro({ ...borrador, id: fila.id }, p.usuarioId);
        if (g.status === 'ejecutado') await guardarBrigadistasPresentes(p.companyId, fila.id, presentesAntes, presentes);
        setPresentesAntes(presentes);
        logAudit({ company_id: p.companyId, action: 'updated', entity_type: 'emergency_drill', entity_id: fila.id, entity_name: origenSimulacro(g),
          details: { evento: g.status === 'ejecutado' ? 'simulacro_resultado' : 'simulacro_editado', estado: g.status } });
        await recargar();
        setBorrador(editableDe(g));
        setOk(g.status === 'ejecutado' ? 'Resultado guardado.' : 'Simulacro guardado.');
      }
      p.onCambio();
    } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  }

  async function confirmarBorrar() {
    if (!borrar) return;
    setOcupado(true); setError('');
    try {
      await borrarSimulacro(borrar);
      logAudit({ company_id: p.companyId, action: 'deleted', entity_type: 'emergency_drill', entity_id: borrar.id, entity_name: origenSimulacro(borrar), details: { evento: 'simulacro_borrado' } });
      setBorrar(null); setSeleccion(null); await recargar(); p.onCambio(); setOk('Simulacro borrado.');
    } catch (e) { setError(msg(e)); setBorrar(null); } finally { setOcupado(false); }
  }

  async function subirEvid(archivo: File | null) {
    if (!archivo || !fila) return;
    setOcupado(true); setError('');
    try { await subirEvidenciaSimulacro(fila, archivo, p.usuarioId); await recargar(); setOk('Evidencia subida.'); }
    catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  }
  async function confirmarBorrarEvid() {
    if (!borrarEvid) return;
    setOcupado(true); setError('');
    try { await borrarEvidenciaSimulacro(borrarEvid); setBorrarEvid(null); await recargar(); setOk('Evidencia borrada.'); }
    catch (e) { setError(msg(e)); setBorrarEvid(null); } finally { setOcupado(false); }
  }
  async function abrirArchivo(ruta: string, nombre?: string) {
    setError('');
    try { descargarUrl(await urlArchivoSimulacro(ruta, nombre), !nombre); } catch (e) { setError(msg(e)); }
  }

  async function guardarPropuesta(pr: PropuestaSimulacro) {
    const f = propuestasForm[pr.sourceKey] ?? { obligatoria: false, cargo: '', fecha: '', responsableId: '' };
    const a: AccionEditable = { ...editableDesdePropuesta(pr, f.obligatoria), responsableCargo: f.cargo, fecha: f.fecha, responsableId: f.responsableId || null };
    const errores = validarAccion(a);
    if (errores.length) { setError(errores.join(' ')); return; }
    setError('');
    if (await p.onGuardarAccion(a)) setOk('Acción de mejora guardada en el plan de acción (Revisión y aprobación › Plan de acción).');
  }
  async function guardarHallazgo() {
    if (!fila || !hallazgo.trim()) return;
    const clave = siguienteClaveManual(fila.id, Array.from(clavesGuardadas));
    const a: AccionEditable = { ...editableDesdePropuesta({ sourceKey: clave, norma: origenSimulacro(fila), articulo: 'Hallazgo del simulacro', descripcion: hallazgo.trim() }, false) };
    if (await p.onGuardarAccion(a)) { setHallazgo(''); setOk('Acción de mejora guardada en el plan de acción (Revisión y aprobación › Plan de acción).'); }
  }

  // Datos del PDF del informe (borrador o firmado).
  const datosInforme = (s: FilaSimulacro, firmas: FirmaInformePDF[], borradorPdf: boolean) => ({
    empresa: p.empresa, planCodigo: p.plan.code, simulacro: s, alcance: alcanceTexto(s, p.sectores),
    amenaza: p.amenazas.find(a => a.clave === s.threat_key)?.nombre ?? null,
    coordinador: [nombreTrabajador(s.coordinator_worker_id), s.coordinator_role].filter(Boolean).join(' · '),
    comparaciones: comparar(s, presentes.length), brigadistas: presentes.map(nombreTrabajador).filter(Boolean),
    acciones: accionesDe(s.id), evidencias: evidenciasDe(s.id), firmas, metodo: borradorPdf ? null : metodo, borrador: borradorPdf, fecha: new Date(),
  });

  function descargarBorradorInforme() {
    if (!actual) return;
    setError('');
    try { generarInformeSimulacroPDF(datosInforme(actual, [], true)).save(nombreArchivoInforme(actual).replace('.pdf', '_borrador.pdf')); }
    catch (e) { setError(`No se pudo generar el borrador: ${msg(e)}`); }
  }

  const faltanFirma = fila ? [
    ...(sucio || presentesCambiaron ? ['Guarda los cambios antes de firmar.'] : []),
    ...faltantesParaFirmar(fila, firmantesActivos(firmantes).map(rol => ({ rol, ...firmantes[rol] }))),
    ...faltaTrazo(metodo, firmantes),
  ] : [];

  async function firmarUI() {
    if (!fila || faltanFirma.length) return;
    setOcupado(true); setError('');
    try {
      const f = await prepararFirmas(metodo, firmantes, pads, x => contenidoParaFirma(fila, x));
      const pdf = generarInformeSimulacroPDF(datosInforme(fila, f.pdf, false)).output('blob');
      const g = await firmarInforme(fila, metodo, f.subir, pdf, f.hashInforme);
      logAudit({ company_id: p.companyId, action: 'updated', entity_type: 'emergency_drill', entity_id: fila.id, entity_name: origenSimulacro(fila),
        details: { evento: 'informe_firmado', metodo, firmantes: f.subir.map(x => x.rol), hash: f.hashInforme } });
      setFirmar(false); setFirmantes({ sst: firmanteVacio(), representante: firmanteVacio() });
      await recargar(); setBorrador(editableDe(g)); p.onCambio();
      setOk('Informe firmado. El simulacro quedó bloqueado.');
    } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  }

  async function reabrirUI() {
    if (!fila) return;
    setOcupado(true); setError('');
    try {
      await reabrirSimulacro(fila.id, motivo);
      setReabrir(false); setMotivo('');
      const l = await recargar();
      const r = l?.find(x => x.id === fila.id);
      if (r) setBorrador(editableDe(r));
      p.onCambio(); setOk('Simulacro reabierto. El motivo quedó en la auditoría.');
    } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  }

  // ── Informe anual ──
  const anios = useMemo(() => Array.from(new Set([hoy.getFullYear(), ...lista.map(s => Number(fechaSimulacro(s).slice(0, 4)))])).sort((a, b) => b - a), [lista, hoy]);
  const datosAnual = (firmas: FirmaInformePDF[], borradorPdf: boolean, version: number) => {
    const delAno = simulacrosDelAno(lista, anio).sort((a, b) => fechaSimulacro(a).localeCompare(fechaSimulacro(b)));
    const r = resumenAnual(lista, p.acciones, anio, hoy);
    const finAno = anio < hoy.getFullYear() ? new Date(anio, 11, 31) : hoy;
    const ids = new Set(delAno.map(s => s.id));
    return {
      empresa: p.empresa, planCodigo: p.plan.code, resumen: r, version, borrador: borradorPdf, fecha: new Date(), firmas, metodo: borradorPdf ? null : metodo,
      frecuencia: estadoPO17(lista.filter(s => fechaSimulacro(s) <= hoyIso(finAno)), finAno, p.quito).trazas,
      simulacros: delAno.map(s => ({
        fecha: fechaEc(fechaSimulacro(s)), tipo: tipoTexto(s), estado: ETIQUETA_ESTADO[s.status], alcance: alcanceTexto(s, p.sectores),
        tiempo: formatoDuracion(tiempoTotal(s).segundos), firmado: !!s.report_signed_at, semaforo: ETIQUETA_SEMAFORO[semaforo(s, comparar(s, null))],
      })),
      acciones: p.acciones.filter(a => esClaveSimulacro(a.source_key) && ids.has(a.source_key!.split(':')[1])).map(a => ({
        descripcion: a.description, origen: a.norma ?? 'Simulacro', estado: ETIQUETA_ESTADO_ACCION[a.status],
        vencida: a.status !== 'cerrada' && !!a.due_date && a.due_date < hoyIso(hoy),
      })),
    };
  };
  const versionAnual = Math.max(0, ...anuales.filter(a => a.year === anio).map(a => a.version)) + 1;
  const faltanAnual = [
    ...(!firmantes.sst.nombre.trim() || !firmantes.sst.cedula.trim() ? ['Falta la firma del responsable de SST (nombre y cédula).'] : []),
    ...firmantesActivos(firmantes).filter(rol => firmantes[rol].cedula.trim() && !/^\d{10}$/.test(firmantes[rol].cedula.trim()))
      .map(rol => `La cédula de ${ETIQUETA_ROL_FIRMA[rol]} debe tener 10 dígitos.`),
    ...faltaTrazo(metodo, firmantes),
  ];
  function descargarBorradorAnual() {
    setError('');
    try { generarInformeAnualPDF(datosAnual([], true, versionAnual)).save(`Informe_anual_simulacros_${anio}_borrador.pdf`); }
    catch (e) { setError(`No se pudo generar el borrador: ${msg(e)}`); }
  }
  async function firmarAnualUI() {
    if (faltanAnual.length) return;
    setOcupado(true); setError('');
    try {
      const resumen = resumenAnual(lista, p.acciones, anio, hoy);
      const f = await prepararFirmas(metodo, firmantes, pads, x => JSON.stringify({ empresa: p.companyId, year: anio, version: versionAnual, resumen, firmante: x }));
      const pdf = generarInformeAnualPDF(datosAnual(f.pdf, false, versionAnual)).output('blob');
      const g = await guardarInformeAnual({ companyId: p.companyId, planId: p.plan.id, year: anio, version: versionAnual, metodo, firmas: f.subir, pdf,
        hash: f.hashInforme, resumen: resumen as unknown as Record<string, unknown>, usuarioId: p.usuarioId });
      logAudit({ company_id: p.companyId, action: 'created', entity_type: 'emergency_drill_annual_report', entity_id: g.id,
        entity_name: `Informe anual de simulacros ${anio} · versión ${versionAnual}`, details: { evento: 'informe_anual_firmado', year: anio, version: versionAnual } });
      setAnualAbierto(false); setFirmantes({ sst: firmanteVacio(), representante: firmanteVacio() });
      await recargar(); setOk(`Informe anual ${anio} firmado y guardado (versión ${versionAnual}).`);
    } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  }

  if (cargando) return <div className="pe2-card">Cargando simulacros…</div>;
  if (errorCarga) return <div className="pe2-error" role="alert">No se pudieron leer los simulacros: {errorCarga}</div>;

  const t = actual ? tiempoTotal(actual) : null;
  const esperados = ocupantesEsperados(p.sectores, borrador.scope_all, borrador.scope_sector_ids);

  return (
    <div className="pe2-sim">
      {error && <div className="pe2-error" role="alert">{error}</div>}
      {ok && <div className="pe2-ok">{ok}</div>}

      <section className="pe2-card" aria-label="Simulacros">
        <h3>Simulacros</h3>
        <p className="pe2-nota" style={{ margin: 0 }}>
          PO-17: {po17.resumen} Al menos un simulacro al año, con su informe firmado (DE 394, Art. 68{p.quito ? '; en Quito la RTQ 3 pide la misma frecuencia' : ''}).
        </p>
        <div className="pe2-acciones">
          {p.puedeEditar && <button type="button" className="pe2-btn primario" disabled={ocupado} onClick={() => abrir('nuevo')}>Programar simulacro</button>}
          <label className="pe2-campo pe2-campo-corto"><span>Año del informe</span>
            <select value={anio} onChange={e => setAnio(Number(e.target.value))}>{anios.map(a => <option key={a} value={a}>{a}</option>)}</select>
          </label>
          <button type="button" className="pe2-btn" disabled={ocupado} onClick={() => { setOk(''); setError(''); setAnualAbierto(true); }}>Informe anual {anio}</button>
        </div>
        {lista.length === 0 ? <p className="pe2-nota">Aún no hay simulacros.</p> : (
          <div className="pe2-tabla-sim" role="table" aria-label="Lista de simulacros">
            <div role="row" className="cabeza">
              <span role="columnheader">Estado</span><span role="columnheader">Fecha</span><span role="columnheader">Tipo</span>
              <span role="columnheader">Alcance</span><span role="columnheader">Tiempo total</span><span role="columnheader">Cumplimiento</span><span role="columnheader" />
            </div>
            {lista.map(s => {
              const sem = semaforo(s, comparar(s, null));
              return (
                <div role="row" key={s.id} className={seleccion === s.id ? 'on' : ''}>
                  <span role="cell">{ETIQUETA_ESTADO[s.status]}{s.report_signed_at ? ' · firmado' : ''}</span>
                  <span role="cell">{fechaEc(fechaSimulacro(s))}</span>
                  <span role="cell">{tipoTexto(s)}</span>
                  <span role="cell">{alcanceTexto(s, p.sectores)}</span>
                  <span role="cell">{formatoDuracion(tiempoTotal(s).segundos)}</span>
                  <span role="cell">{sem === 'pendiente' ? ETIQUETA_SEMAFORO.pendiente : <Semaforo estado={sem} />}</span>
                  <span role="cell"><button type="button" className="pe2-btn" onClick={() => abrir(s)}>{p.puedeEditar && !s.report_signed_at ? 'Abrir' : 'Ver'}</button></span>
                </div>
              );
            })}
          </div>
        )}
        {anuales.length > 0 && (
          <>
            <h4>Informes anuales firmados</h4>
            <ul className="pe2-vista-lista" aria-label="Informes anuales">
              {anuales.map(a => (
                <li key={a.id} className="pe2-fila-editable">
                  <span>Año {a.year} · versión {a.version} · {fechaEc(a.signed_at)} · {ETIQUETA_METODO_FIRMA[a.method]}</span>
                  <span />
                  <button type="button" className="pe2-btn" onClick={() => abrirArchivo(a.pdf_path, `Informe_anual_simulacros_${a.year}_v${a.version}.pdf`)}>Descargar</button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {seleccion && (
        <section className="pe2-card" aria-label={seleccion === 'nuevo' ? 'Programar simulacro' : 'Simulacro'}>
          <h3>{seleccion === 'nuevo' ? 'Programar simulacro' : `${tipoTexto(actual!)} · ${fechaEc(fechaSimulacro(actual!))} · ${ETIQUETA_ESTADO[actual!.status]}`}</h3>
          {bloqueado && fila && (
            <div className="pe2-aviso" role="status">
              Informe firmado el {new Date(fila.report_signed_at as string).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' })} ({fila.report_method ? ETIQUETA_METODO_FIRMA[fila.report_method] : ''}): el simulacro está bloqueado.
              {p.puedeBorrar ? ' Para corregirlo, reábrelo con un motivo.' : ' Solo el administrador puede reabrirlo.'}
            </div>
          )}
          {fila && fila.reopen_history.length > 0 && (
            <p className="pe2-nota">Reabierto {fila.reopen_history.length} vez(ces). Último motivo: "{fila.reopen_history[fila.reopen_history.length - 1].motivo}".</p>
          )}

          <h4>Programación</h4>
          <div className="pe2-grid">
            <Campo etiqueta="Tipo de simulacro">
              <select value={borrador.drill_type} disabled={ro} onChange={e => set('drill_type', e.target.value as TipoSimulacro)}>
                {TIPOS_SIMULACRO.map(x => <option key={x} value={x}>{ETIQUETA_TIPO[x]}</option>)}
              </select>
            </Campo>
            {borrador.drill_type === 'otro' && (
              <Campo etiqueta="¿Qué tipo?"><input type="text" value={borrador.drill_type_other ?? ''} disabled={ro} onChange={e => set('drill_type_other', e.target.value)} /></Campo>
            )}
            <Campo etiqueta="Fecha programada">
              <input type="date" value={borrador.scheduled_date ?? ''} disabled={ro} onChange={e => set('scheduled_date', e.target.value || null)} />
            </Campo>
            <Campo etiqueta="Hora de inicio"><input type="time" value={borrador.start_time?.slice(0, 5) ?? ''} disabled={ro} onChange={e => set('start_time', hora(e.target.value))} /></Campo>
            <Campo etiqueta="Amenaza del plan (opcional)">
              <select value={borrador.threat_key ?? ''} disabled={ro} onChange={e => set('threat_key', e.target.value || null)}>
                <option value="">Ninguna</option>
                {p.amenazas.map(a => <option key={a.clave} value={a.clave}>{a.nombre}</option>)}
              </select>
            </Campo>
            <Campo etiqueta="Lugar"><input type="text" value={borrador.location ?? ''} disabled={ro} onChange={e => set('location', e.target.value)} /></Campo>
            <Campo etiqueta="Aviso previo">
              <select value={borrador.announced === null ? '' : borrador.announced ? 'si' : 'no'} disabled={ro}
                onChange={e => set('announced', e.target.value === '' ? null : e.target.value === 'si')}>
                <option value="si">Con aviso previo</option><option value="no">Sin aviso</option>
              </select>
            </Campo>
            <Campo etiqueta="Coordinador (trabajador)">
              <select value={borrador.coordinator_worker_id ?? ''} disabled={ro} onChange={e => set('coordinator_worker_id', e.target.value || null)}>
                <option value="">Ninguno</option>
                {p.trabajadores.map(w => <option key={w.id} value={w.id}>{w.full_name}</option>)}
              </select>
            </Campo>
            <Campo etiqueta="Coordinador (cargo)"><input type="text" value={borrador.coordinator_role ?? ''} disabled={ro} onChange={e => set('coordinator_role', e.target.value)} /></Campo>
            <Campo etiqueta="Observadores"><input type="text" value={borrador.observers ?? ''} disabled={ro} onChange={e => set('observers', e.target.value)} /></Campo>
          </div>
          <div className="pe2-campo">
            <span>Alcance</span>
            <label className="pe2-check"><input type="checkbox" checked={borrador.scope_all} disabled={ro} onChange={e => set('scope_all', e.target.checked)} />Todo el centro</label>
            {!borrador.scope_all && (p.sectores.length === 0 ? <small>El plan no tiene sectores (capítulo 2).</small> : p.sectores.map(s => (
              <label key={s.id} className="pe2-check">
                <input type="checkbox" checked={borrador.scope_sector_ids.includes(s.id)} disabled={ro}
                  onChange={e => set('scope_sector_ids', e.target.checked ? [...borrador.scope_sector_ids, s.id] : borrador.scope_sector_ids.filter(x => x !== s.id))} />
                {s.name}{s.occupants !== null ? ` (${s.occupants} ocupantes)` : ''}
              </label>
            )))}
          </div>
          <Campo etiqueta="Objetivo"><textarea value={borrador.objective ?? ''} disabled={ro} rows={2} onChange={e => set('objective', e.target.value)} /></Campo>
          <Campo etiqueta="Escenario"><textarea value={borrador.scenario ?? ''} disabled={ro} rows={2} onChange={e => set('scenario', e.target.value)} /></Campo>

          {borrador.status === 'ejecutado' && fila && (
            <>
              <h4>Resultado</h4>
              <div className="pe2-grid">
                <Campo etiqueta="Fecha realizada"><input type="date" max={hoyIso(hoy)} value={borrador.performed_date ?? ''} disabled={ro} onChange={e => set('performed_date', e.target.value || null)} /></Campo>
                <Campo etiqueta="Participantes" nota={presentes.length ? `${plural(presentes.length, 'brigadista registrado', 'brigadistas registrados')} como ${presentes.length === 1 ? 'presente' : 'presentes'}.` : undefined}>
                  <NumeroValor valor={borrador.participants_count} onChange={n => set('participants_count', n)} unidad="personas" entero disabled={ro} etiqueta="Participantes" />
                </Campo>
                <Campo etiqueta="Ocupantes esperados" nota={`Suma de los sectores del alcance: ${esperados.total ?? '—'}${esperados.sinDato.length ? ` (sin dato: ${esperados.sinDato.join(', ')})` : ''}. Editable.`}>
                  <NumeroValor valor={borrador.expected_occupants} onChange={n => set('expected_occupants', n)} unidad="personas" entero disabled={ro} etiqueta="Ocupantes esperados" />
                </Campo>
                <Campo etiqueta="Conteo en el punto de encuentro">
                  <NumeroValor valor={borrador.assembly_count} onChange={n => set('assembly_count', n)} unidad="personas" entero disabled={ro} etiqueta="Conteo en el punto de encuentro" />
                </Campo>
              </div>
              <label className="pe2-check"><input type="checkbox" checked={cronometro} disabled={ro} onChange={e => setCronometro(e.target.checked)} />Registrar con cronómetro (minutos y segundos desde la alarma)</label>
              <div className="pe2-grid">
                <Campo etiqueta="Hora de la alarma">
                  <input type="time" step={1} value={borrador.alarm_time ?? ''} disabled={ro} onChange={e => set('alarm_time', hora(e.target.value))} />
                </Campo>
                {cronometro ? (
                  <>
                    <Campo etiqueta="Inicio de la evacuación (min:seg)">
                      <input type="text" placeholder="0:40" value={crono.inicio} disabled={ro || !borrador.alarm_time}
                        onChange={e => { setCrono({ ...crono, inicio: e.target.value }); set('evacuation_start_time', horaDesdeCronometro(borrador.alarm_time ?? '', e.target.value)); }} />
                    </Campo>
                    <Campo etiqueta="Último en llegar (min:seg)">
                      <input type="text" placeholder="3:00" value={crono.llegada} disabled={ro || !borrador.alarm_time}
                        onChange={e => { setCrono({ ...crono, llegada: e.target.value }); set('last_arrival_time', horaDesdeCronometro(borrador.alarm_time ?? '', e.target.value)); }} />
                    </Campo>
                  </>
                ) : (
                  <>
                    <Campo etiqueta="Inicio de la evacuación"><input type="time" step={1} value={borrador.evacuation_start_time ?? ''} disabled={ro} onChange={e => set('evacuation_start_time', hora(e.target.value))} /></Campo>
                    <Campo etiqueta="Último en llegar al punto de encuentro"><input type="time" step={1} value={borrador.last_arrival_time ?? ''} disabled={ro} onChange={e => set('last_arrival_time', hora(e.target.value))} /></Campo>
                  </>
                )}
              </div>
              <p className="pe2-nota" aria-live="polite">
                Tiempo total: <b>{formatoDuracion(t?.segundos ?? null)}</b>{t?.error ? ` · ${t.error}` : ''}
                {horaASegundos(borrador.evacuation_start_time) !== null && horaASegundos(borrador.alarm_time) !== null
                  ? ` · Premovimiento observado: ${formatoDuracion((horaASegundos(borrador.evacuation_start_time) as number) - (horaASegundos(borrador.alarm_time) as number))}` : ''}
              </p>
              {p.brigada && p.brigada.length > 0 && (
                <div className="pe2-campo">
                  <span>Brigadistas presentes ({presentes.length} de {p.brigada.length})</span>
                  {p.brigada.map(w => (
                    <label key={w} className="pe2-check">
                      <input type="checkbox" checked={presentes.includes(w)} disabled={ro}
                        onChange={e => setPresentes(e.target.checked ? [...presentes, w] : presentes.filter(x => x !== w))} />
                      {nombreTrabajador(w) || 'Trabajador sin nombre'}
                    </label>
                  ))}
                </div>
              )}
              <Campo etiqueta="Hallazgos e incidencias"><textarea value={borrador.incidents_notes ?? ''} disabled={ro} rows={3} onChange={e => set('incidents_notes', e.target.value)} /></Campo>
              <Campo etiqueta="Lecciones aprendidas"><textarea value={borrador.lessons_learned ?? ''} disabled={ro} rows={2} onChange={e => set('lessons_learned', e.target.value)} /></Campo>
              <Campo etiqueta="Conclusión"><textarea value={borrador.conclusion ?? ''} disabled={ro} rows={2} onChange={e => set('conclusion', e.target.value)} /></Campo>

              <h4>Comparación con el plan</h4>
              {comparaciones.map(c => <TrazaItem key={c.clave} t={c.traza} claveEvaluacion="rset" />)}
            </>
          )}

          {!ro && (
            <div className="pe2-acciones">
              {seleccion !== 'nuevo' && borrador.status === 'programado' && (
                <>
                  <button type="button" className="pe2-btn" disabled={ocupado} onClick={registrarResultado}>Registrar resultado</button>
                  <button type="button" className="pe2-btn" disabled={ocupado} onClick={() => set('status', 'cancelado')}>Marcar como cancelado</button>
                </>
              )}
              <button type="button" className="pe2-btn primario" disabled={ocupado || (!!fila && !sucio && !presentesCambiaron)} onClick={guardar}>
                {ocupado ? 'Guardando…' : seleccion === 'nuevo' ? 'Programar simulacro' : 'Guardar'}
              </button>
              {fila && p.puedeBorrar && <button type="button" className="pe2-btn peligro" disabled={ocupado} onClick={() => setBorrar(fila)}>Borrar simulacro</button>}
              <button type="button" className="pe2-btn" disabled={ocupado} onClick={() => setSeleccion(null)}>Cerrar</button>
            </div>
          )}
          {ro && <div className="pe2-acciones"><button type="button" className="pe2-btn" onClick={() => setSeleccion(null)}>Cerrar</button></div>}

          {fila && fila.status === 'ejecutado' && (
            <>
              <h4>Acciones de mejora</h4>
              {sucio && <p className="pe2-nota">Guarda el resultado para ver las acciones propuestas.</p>}
              {propuestas.length > 0 && !bloqueado && p.puedeEditar && (
                <div className="pe2-aviso" role="status">
                  El simulacro propone {plural(propuestas.length, 'acción', 'acciones')}. No se guardan solas: revisa y confirma cada una.
                </div>
              )}
              {!bloqueado && p.puedeEditar && propuestas.map(pr => {
                const f = propuestasForm[pr.sourceKey] ?? { obligatoria: false, cargo: '', fecha: '', responsableId: '' };
                const setF = (x: Partial<typeof f>) => setPropuestasForm({ ...propuestasForm, [pr.sourceKey]: { ...f, ...x } });
                return (
                  <div key={pr.sourceKey} className="pe2-card pe2-sub-card" aria-label={`Propuesta ${pr.articulo}`}>
                    <p><b>{pr.norma} · {pr.articulo}</b></p>
                    <p>{pr.descripcion}</p>
                    <label className="pe2-check"><input type="checkbox" checked={f.obligatoria} onChange={e => setF({ obligatoria: e.target.checked })} />Obligatoria (exige responsable, fecha y evidencia para cerrarla)</label>
                    <div className="pe2-grid">
                      <Campo etiqueta="Responsable (trabajador)">
                        <select value={f.responsableId} onChange={e => setF({ responsableId: e.target.value })}>
                          <option value="">Ninguno</option>{p.trabajadores.map(w => <option key={w.id} value={w.id}>{w.full_name}</option>)}
                        </select>
                      </Campo>
                      <Campo etiqueta="Responsable (cargo)"><input type="text" value={f.cargo} onChange={e => setF({ cargo: e.target.value })} /></Campo>
                      <Campo etiqueta="Fecha compromiso"><input type="date" value={f.fecha} onChange={e => setF({ fecha: e.target.value })} /></Campo>
                    </div>
                    <div className="pe2-acciones"><button type="button" className="pe2-btn primario" disabled={ocupado} onClick={() => guardarPropuesta(pr)}>Guardar acción</button></div>
                  </div>
                );
              })}
              {!bloqueado && p.puedeEditar && !sucio && (
                <div className="pe2-fila-editable">
                  <Campo etiqueta="Nueva acción desde un hallazgo"><input type="text" value={hallazgo} onChange={e => setHallazgo(e.target.value)} /></Campo>
                  <span />
                  <button type="button" className="pe2-btn" disabled={ocupado || !hallazgo.trim()} onClick={guardarHallazgo}>Agregar acción</button>
                </div>
              )}
              {accionesDe(fila.id).length === 0 ? <p className="pe2-nota">Sin acciones de mejora guardadas.</p> : (
                <ul className="pe2-vista-lista" aria-label="Acciones de mejora guardadas">
                  {accionesDe(fila.id).map(a => (
                    <li key={a.id}>{a.description}{a.is_mandatory ? ' (obligatoria)' : ''} · {ETIQUETA_ESTADO_ACCION[a.status]}{a.due_date ? ` · ${fechaEc(a.due_date)}` : ''} · se sigue en Revisión y aprobación › Plan de acción</li>
                  ))}
                </ul>
              )}

              <h4>Evidencias</h4>
              {evidenciasDe(fila.id).length === 0 ? <p className="pe2-nota">Sin evidencias.</p> : (
                <ul className="pe2-vista-lista" aria-label="Evidencias del simulacro">
                  {evidenciasDe(fila.id).map(e => (
                    <li key={e.id} className="pe2-fila-editable">
                      <span>{e.file_name} · {tamanoLegible(e.size_bytes)}</span>
                      <button type="button" className="pe2-btn" onClick={() => abrirArchivo(e.storage_path)}>Abrir</button>
                      {p.puedeBorrar && !bloqueado ? <button type="button" className="pe2-btn peligro" disabled={ocupado} onClick={() => setBorrarEvid(e)}>Borrar</button> : <span />}
                    </li>
                  ))}
                </ul>
              )}
              {!ro && (
                <Campo etiqueta="Subir foto o PDF (hasta 10 MB)">
                  <input type="file" accept={ACEPTA_EVIDENCIA} disabled={ocupado} onChange={e => { subirEvid(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                </Campo>
              )}

              <h4>Informe</h4>
              <div className="pe2-acciones">
                <button type="button" className="pe2-btn" onClick={descargarBorradorInforme}>Descargar borrador del informe</button>
                {bloqueado && fila.report_pdf_path && (
                  <button type="button" className="pe2-btn primario" onClick={() => abrirArchivo(fila.report_pdf_path as string, nombreArchivoInforme(fila))}>Descargar informe firmado</button>
                )}
                {!bloqueado && p.puedeEditar && <button type="button" className="pe2-btn primario" disabled={ocupado} onClick={() => { setOk(''); setFirmar(true); }}>Firmar informe</button>}
                {bloqueado && p.puedeBorrar && <button type="button" className="pe2-btn peligro" disabled={ocupado} onClick={() => { setMotivo(''); setReabrir(true); }}>Reabrir simulacro</button>}
              </div>
            </>
          )}
        </section>
      )}

      {firmar && fila && (
        <div className="pe2-modal-fondo">
          <div className="pe2-modal" role="dialog" aria-modal="true" aria-labelledby="pe2-firmar-sim">
            <h3 id="pe2-firmar-sim">Firmar el informe del simulacro</h3>
            <p className="pe2-nota" style={{ margin: 0 }}>Al firmar se guarda el PDF y el simulacro queda bloqueado. Cada firma lleva nombre, cédula, fecha y hora, y un hash SHA-256 del contenido.</p>
            {propuestas.length > 0 && <div className="pe2-aviso">Hay {plural(propuestas.length, 'acción propuesta', 'acciones propuestas')} sin confirmar: no {propuestas.length === 1 ? 'saldrá' : 'saldrán'} en el informe.</div>}
            <FormFirmas metodo={metodo} setMetodo={setMetodo} firmantes={firmantes} setFirmantes={setFirmantes} pads={pads} ocupado={ocupado} />
            {faltanFirma.length > 0 && (
              <div className="pe2-aviso" role="status">Para firmar falta:<ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{faltanFirma.map((f, i) => <li key={i}>{f}</li>)}</ul></div>
            )}
            <div className="pe2-acciones">
              <button type="button" className="pe2-btn" disabled={ocupado} onClick={() => setFirmar(false)}>Cancelar</button>
              <button type="button" className="pe2-btn primario" disabled={ocupado || faltanFirma.length > 0} onClick={firmarUI}>{ocupado ? 'Firmando…' : 'Firmar informe'}</button>
            </div>
          </div>
        </div>
      )}

      {reabrir && fila && (
        <div className="pe2-modal-fondo">
          <div className="pe2-modal" role="dialog" aria-modal="true" aria-labelledby="pe2-reabrir-sim">
            <h3 id="pe2-reabrir-sim">Reabrir el simulacro</h3>
            <p>Se quitará la firma del informe para corregirlo; la firma anterior y el PDF quedan guardados en el historial y el motivo en la auditoría. Después hay que firmarlo de nuevo.</p>
            <Campo etiqueta="Motivo (al menos 10 caracteres)"><textarea value={motivo} rows={3} disabled={ocupado} onChange={e => setMotivo(e.target.value)} /></Campo>
            <div className="pe2-acciones">
              <button type="button" className="pe2-btn" disabled={ocupado} onClick={() => setReabrir(false)}>Cancelar</button>
              <button type="button" className="pe2-btn peligro" disabled={ocupado || motivo.trim().length < 10} onClick={reabrirUI}>{ocupado ? 'Reabriendo…' : 'Reabrir'}</button>
            </div>
          </div>
        </div>
      )}

      {anualAbierto && (
        <div className="pe2-modal-fondo">
          <div className="pe2-modal" role="dialog" aria-modal="true" aria-labelledby="pe2-anual">
            <h3 id="pe2-anual">Informe anual de simulacros {anio} · versión {versionAnual}</h3>
            {(() => {
              const r = resumenAnual(lista, p.acciones, anio, hoy);
              return (
                <ul className="pe2-vista-lista">
                  <li>Realizados: {r.realizados} ({r.firmados} con el informe firmado) · programados: {r.programados} · cancelados: {r.cancelados}</li>
                  <li>Frecuencia mínima (al menos uno al año, firmado): {r.cumpleFrecuencia ? 'cumple' : 'no cumple'}</li>
                  <li>Tiempos: mínimo {formatoDuracion(r.tiempos.minimo_s)} · máximo {formatoDuracion(r.tiempos.maximo_s)} · promedio {formatoDuracion(r.tiempos.promedio_s)}</li>
                  <li>Acciones de mejora: {r.acciones.abiertas} abiertas · {r.acciones.cerradas} cerradas · {r.acciones.vencidas} vencidas</li>
                </ul>
              );
            })()}
            <div className="pe2-acciones"><button type="button" className="pe2-btn" onClick={descargarBorradorAnual}>Descargar borrador</button></div>
            {p.puedeEditar ? (
              <>
                <p className="pe2-nota" style={{ margin: 0 }}>Firmado, se guarda como una versión que no se puede cambiar ni borrar.</p>
                <FormFirmas metodo={metodo} setMetodo={setMetodo} firmantes={firmantes} setFirmantes={setFirmantes} pads={pads} ocupado={ocupado} />
                {faltanAnual.length > 0 && (
                  <div className="pe2-aviso" role="status">Para firmar falta:<ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{faltanAnual.map((f, i) => <li key={i}>{f}</li>)}</ul></div>
                )}
              </>
            ) : <p className="pe2-nota">Solo el administrador o el responsable pueden firmar el informe anual.</p>}
            <div className="pe2-acciones">
              <button type="button" className="pe2-btn" disabled={ocupado} onClick={() => setAnualAbierto(false)}>Cancelar</button>
              {p.puedeEditar && <button type="button" className="pe2-btn primario" disabled={ocupado || faltanAnual.length > 0} onClick={firmarAnualUI}>{ocupado ? 'Guardando…' : 'Firmar y guardar'}</button>}
            </div>
          </div>
        </div>
      )}

      <ModalConfirmar abierto={!!borrar} titulo="Borrar simulacro" ocupado={ocupado} textoConfirmar="Borrar simulacro"
        mensaje={borrar ? `Se borrará el simulacro del ${fechaEc(fechaSimulacro(borrar))} con sus participantes y evidencias. Sus acciones de mejora quedan en el plan de acción.` : ''}
        onConfirmar={confirmarBorrar} onCancelar={() => setBorrar(null)} />
      <ModalConfirmar abierto={!!borrarEvid} titulo="Borrar evidencia" ocupado={ocupado} mensaje={borrarEvid ? `Se borrará "${borrarEvid.file_name}".` : ''}
        onConfirmar={confirmarBorrarEvid} onCancelar={() => setBorrarEvid(null)} />
    </div>
  );
}
